import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import { positionKindTone } from '../components/PositionGrid';
import type { MemberClubView, PrimaryTokenView, ProjectedSlotView } from '../lib/lookup-types';
import {
  emptySnapshot,
  type ActivityRecord,
  type AppDataSnapshot,
  type CycleView,
  type Era,
  type IndexerStatusView,
  type MatrixNode,
  type PlacementRulesView,
  type SlotSummaryView,
  DEFAULT_PLACEMENT_RULES,
} from '../data/view-model';
import { SLOT_HALF, compareSlots, highestSlot, isSubSlot, slotTitle } from '../lib/slots';
import { useWallet } from './WalletContext';

interface ApiMember {
  bdId: string;
  walletAddress: string;
  sponsorBdId: string | null;
  birthEra: 'standard' | 'half' | 'quarter';
  rank: string;
  status: 'pending' | 'active' | 'suspended';
  joinedAt: string;
}

interface ApiDashboard {
  projectionNotice: string;
  member: ApiMember;
  /** Owner 2026-10-11: every figure from indexed contract events (JoinPlaced, recycle seats, vault RewardPaid / Claimed). */
  stats: {
    teamMembers: number;
    directMembers: number;
    completedCycles: number;
    activeSlots: number;
    totalEarnedAtomic: string;
    /** The vault claimable balance (kept name). */
    redeemableAtomic: string;
  };
  slots: Array<{
    slotNumber: number;
    slotType: 'standard' | 'half' | 'quarter';
    status: 'locked' | 'active' | 'completed';
    requiredAmountAtomic: string;
  }>;
  recentTransactions: Array<{
    txHash: string;
    kind: string;
    status: 'pending' | 'confirmed' | 'failed' | 'reorged';
    amountAtomic: string;
    blockNumber: number | null;
    observedAt: string;
  }>;
  currentCycle: {
    cycleNumber: number;
    totalPositions: number;
    completedPositions: number;
  } | null;
  tokens: Array<{
    tokenCode: string;
    state: 'available' | 'assigned' | 'redeemed' | 'revoked';
    providerCode: string | null;
    createdAt: string;
    /** On-chain Primary Token status label. */
    status?: string;
  }>;
  /** Name kept: every slot the contract activated for this ID, built from indexed SeatFilled events (source 'onchain'). */
  projectedSlots?: ProjectedSlotView[];
  /** rules/rule13.txt: Leaders Club standing and received club income. */
  club?: MemberClubView;
  /** rules/rule14.txt: Primary Token record. */
  primaryToken?: PrimaryTokenView;
}

interface ApiNetwork {
  rootBdId: string;
  nodes: Array<{
    bdId: string;
    sponsorBdId: string | null;
    birthEra: 'standard' | 'half' | 'quarter';
    status: 'pending' | 'active' | 'suspended';
    joinedAt: string;
    depth: number;
    activeSlot: number;
    confirmedVolumeAtomic: string;
  }>;
}

interface ApiProvider {
  code: string;
  name: string;
  active: boolean;
  governanceStatus: 'approved' | 'review' | 'suspended';
}

interface ApiRankPool {
  rank: string;
  allocationBps: number;
  participants: number;
  distributedAmountAtomic: string;
  nextDistributionAt: string;
}

interface ApiRpcHealth {
  id: string;
  priority: number;
  healthy: boolean;
  latencyMs: number | null;
  consecutiveFailures: number;
  lastCheckedAt: string | null;
}

interface ApiPublicStats {
  totalMembers: number;
  activeMembers: number;
  completedCycles: number;
  confirmedTransactions: number;
  distributedAtomic: string;
}

export type DataMode = 'loading' | 'authenticated' | 'demo' | 'empty' | 'stale' | 'error';

interface AppDataContextValue extends AppDataSnapshot {
  mode: DataMode;
  error: string | null;
  refreshedAt: string | null;
  activeBdId: string | null;
  selectActiveId(bdId: string): void;
  refresh(): void;
  matrixForCycle(slotNumber: number, cycle: number): MatrixNode[];
  /** Live placement rule numbers (admin-set senior sponsor check limit) for every surface that explains a fallback. */
  placementRules: PlacementRulesView;
  /** rules/rule13.txt: the active member's Leaders Club standing (null in demo mode). */
  club: MemberClubView | null;
  /** rules/rule14.txt: the active member's Primary Token record (null in demo mode). */
  primaryToken: PrimaryTokenView | null;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

export function atomicToDecimal(value: string, decimals = 18): string {
  if (!/^\d+$/.test(value)) return '0';
  const padded = value.padStart(decimals + 1, '0');
  const whole = padded.slice(0, -decimals);
  const fraction = padded.slice(-decimals).replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
}

function era(value: 'standard' | 'half' | 'quarter'): Era {
  return value.toUpperCase() as Era;
}

function transactionType(kind: string): ActivityRecord['type'] {
  const normalized = kind.toLowerCase();
  if (normalized.includes('join')) return 'JOIN';
  if (normalized.includes('sponsor')) return 'SPONSOR';
  if (normalized.includes('slot') || normalized.includes('placement')) return 'PLACEMENT';
  if (normalized.includes('upgrade')) return 'UPGRADE';
  if (normalized.includes('recycle') || normalized.includes('cycle')) return 'RECYCLE';
  if (normalized.includes('token')) return 'TOKEN';
  if (normalized.includes('redemption')) return 'REDEMPTION';
  return 'SYSTEM';
}

function transactionStatus(status: ApiDashboard['recentTransactions'][number]['status']): ActivityRecord['status'] {
  if (status === 'confirmed') return 'Confirmed';
  if (status === 'failed') return 'Failed';
  if (status === 'reorged') return 'Reorged';
  return 'Pending';
}

function dailyActivity(records: ActivityRecord[]): number[] {
  const result = Array.from({ length: 14 }, () => 0);
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  for (const record of records) {
    const observed = new Date(record.timestamp);
    observed.setUTCHours(0, 0, 0, 0);
    const daysAgo = Math.floor((today.getTime() - observed.getTime()) / 86_400_000);
    if (daysAgo >= 0 && daysAgo < result.length) result[result.length - 1 - daysAgo]! += 1;
  }
  return result;
}

function snapshotFromApi(
  dashboard: ApiDashboard,
  network: ApiNetwork,
  cycles: CycleView[],
  publicStats: ApiPublicStats | null,
  providers: ApiProvider[] | null,
  rankPools: ApiRankPool[] | null,
  rpcHealth: ApiRpcHealth[] | null,
  indexer: IndexerStatusView | null,
  memberIds: string[],
): AppDataSnapshot {
  const currentCycle = dashboard.currentCycle;
  const activity: ActivityRecord[] = dashboard.recentTransactions.map((record, index) => ({
    id: `${record.txHash}-${index}`,
    type: transactionType(record.kind),
    memberId: dashboard.member.bdId,
    amount: atomicToDecimal(record.amountAtomic),
    status: transactionStatus(record.status),
    timestamp: record.observedAt,
    hash: record.txHash,
    blockNumber: record.blockNumber,
  }));
  const selectedEra = era(dashboard.member.birthEra);
  // Only slot views the API built from the contract's events (source 'onchain') are shown; nothing else is drawn.
  const projectedSlots = (dashboard.projectedSlots ?? []).filter((view) => view.source === 'onchain');
  const club = dashboard.club ?? null;
  const primaryToken = dashboard.primaryToken ?? null;
  const projectedBySlot = new Map(projectedSlots.map((slot) => [slot.slotNumber, slot]));
  // Owner 2026-10-11: a slot card shows only what the chain did. The API's slot views are exactly the slots the
  // contract activated (source 'onchain', seats from SeatFilled); a slot without one is locked. Seats, recycles and the
  // owner seats' amounts come from that view - never from a constant or from another slot's cycle.
  const fromView = (view: ProjectedSlotView | undefined) => {
    const latest = view?.cycles[view.cycles.length - 1];
    const filled = latest ? new Set(latest.positions.filter((item) => item.occupantId).map((item) => item.position)).size : 0;
    return {
      cycle: view?.currentCycleNumber ?? 0,
      placements: filled,
      /** Slot-owner seats filled on-chain, every cycle. */
      received: view ? view.cycles.reduce((sum, item) => sum + (item.ownerSeats ?? 0), 0) : 0,
      recycled: view ? Math.max(0, view.currentCycleNumber - 1) : 0,
      /** Sum of the SeatFilled amounts of those owner seats. */
      earning: view ? atomicToDecimal(view.ownerEarnedTotalAtomic ?? '0') : '0',
      progress: Math.min(100, (filled / 12) * 100),
    };
  };
  const slots: SlotSummaryView[] = dashboard.slots.map((slot) => {
    const view = projectedBySlot.get(slot.slotNumber);
    return {
      number: slot.slotNumber,
      label: `Slot ${slot.slotNumber}`,
      // The contract's slotValue(n) (API: EconomicsConfigured / SeatFilled amounts).
      price: atomicToDecimal(slot.requiredAmountAtomic),
      // Activated on-chain: an indexed slot activation, or an on-chain slot view (seats from SeatFilled).
      active: slot.status !== 'locked' || view !== undefined,
      ...fromView(view),
      status: slot.status,
    };
  });
  // Half / Quarter era (rule 16 Section I): Slot ¼ / Slot ½ come before Slot 1 when this ID has them, from their on-chain
  // slot view (optionally virtual). Price = the contract's slotValue: Slot 1's value / 2 (½) or / 4 (¼).
  const slotOneValue = (() => {
    const raw = dashboard.slots.find((slot) => slot.slotNumber === 1)?.requiredAmountAtomic ?? '0';
    return /^\d+$/.test(raw) ? BigInt(raw) : 0n;
  })();
  const subSlots = projectedSlots
    .filter((slot) => isSubSlot(slot.slotNumber))
    .sort((a, b) => compareSlots(a.slotNumber, b.slotNumber))
    .map((view): SlotSummaryView => ({
      number: view.slotNumber,
      label: slotTitle(view.slotNumber),
      price: atomicToDecimal((view.slotNumber === SLOT_HALF ? slotOneValue >> 1n : slotOneValue >> 2n).toString()),
      active: true,
      virtual: view.virtual === true,
      ...fromView(view),
      status: 'active',
    }));
  slots.unshift(...subSlots);
  const networkNodes = network.nodes.map((node) => ({
    id: node.bdId,
    parent: node.sponsorBdId,
    depth: node.depth,
    volume: atomicToDecimal(node.confirmedVolumeAtomic),
  }));
  const directPartners = network.nodes.filter((node) => node.depth === 1).map((node) => ({
    id: node.bdId,
    era: era(node.birthEra),
    joined: node.joinedAt,
    activeSlot: node.activeSlot,
    volume: atomicToDecimal(node.confirmedVolumeAtomic),
    status: node.status,
  }));
  const rankColors = ['#52d3ff', '#9c7dff', '#e466ff', '#ffae6c'];

  return {
    member: {
      id: dashboard.member.bdId,
      wallet: dashboard.member.walletAddress,
      sponsorId: dashboard.member.sponsorBdId,
      era: selectedEra,
      joinedAt: dashboard.member.joinedAt,
      // Highest active slot by progression rank (Slot ½ = 50 is below Slot 1); virtual sub-slots do not count.
      activeSlot: highestSlot(slots.filter((slot) => slot.active && !slot.virtual).map((slot) => slot.number)),
      currentCycle: currentCycle?.cycleNumber ?? 0,
      rank: dashboard.member.rank,
      status: dashboard.member.status,
    },
    memberIds: memberIds.length > 0 ? memberIds : [dashboard.member.bdId],
    slots,
    projectedSlots,
    club,
    primaryToken,
    activity,
    chartData: dailyActivity(activity),
    partners: directPartners,
    generationNodes: networkNodes,
    // The API maps the token contract's record (Primary Token) into this list; status / ratio / unfreeze are on-chain.
    tokens: dashboard.tokens.map((token) => ({
      tokenId: token.tokenCode,
      memberId: dashboard.member.bdId,
      state: token.status ?? token.state.charAt(0).toUpperCase() + token.state.slice(1),
      era: selectedEra.charAt(0) + selectedEra.slice(1).toLowerCase(),
      ratio: primaryToken?.minted ? (primaryToken.currentRatio ?? primaryToken.originalRatio) : selectedEra === 'STANDARD' ? '1.00×' : selectedEra === 'HALF' ? '0.50×' : '0.25×',
      issued: token.createdAt,
      unlocks: primaryToken?.minted ? primaryToken.unfreezeAt : null,
      progress: 0,
      providerCode: token.providerCode,
    })),
    providers: (providers ?? []).map((provider) => ({
      code: provider.code,
      name: provider.name,
      icon: provider.code.slice(0, 2).toUpperCase(),
      multiplier: 'On-chain terms',
      model: provider.governanceStatus,
      eras: ['See active version'],
      active: provider.active,
      governanceStatus: provider.governanceStatus,
    })),
    ranks: (rankPools ?? []).map((rank, index) => ({
      rank: rank.rank,
      level: index + 1,
      target: rank.participants,
      current: rank.participants,
      share: `${rank.allocationBps / 100}%`,
      unlocked: false,
      color: rankColors[index % rankColors.length]!,
      distributedAmount: atomicToDecimal(rank.distributedAmountAtomic),
      nextDistributionAt: rank.nextDistributionAt,
    })),
    rpcProviders: (rpcHealth ?? []).map((provider) => ({
      id: provider.id,
      name: provider.id.replace(/^rpc-/, '').replace(/\b\w/g, (character) => character.toUpperCase()),
      state: provider.healthy ? 'healthy' : 'degraded',
      latency: provider.latencyMs,
      traffic: 0,
      priority: provider.priority,
      failures: provider.consecutiveFailures,
      lastCheckedAt: provider.lastCheckedAt,
    })),
    cycles,
    dashboardStats: {
      teamMembers: dashboard.stats.teamMembers,
      directMembers: dashboard.stats.directMembers,
      completedCycles: dashboard.stats.completedCycles,
      activeSlots: dashboard.stats.activeSlots,
      totalEarned: atomicToDecimal(dashboard.stats.totalEarnedAtomic),
      redeemable: atomicToDecimal(dashboard.stats.redeemableAtomic),
    },
    publicStats: publicStats ? {
      totalMembers: publicStats.totalMembers,
      activeMembers: publicStats.activeMembers,
      completedCycles: publicStats.completedCycles,
      confirmedTransactions: publicStats.confirmedTransactions,
      distributed: atomicToDecimal(publicStats.distributedAtomic),
    } : emptySnapshot.publicStats,
    indexer: indexer ?? { enabled: false },
    projectionNotice: dashboard.projectionNotice,
  };
}

async function loadDevelopmentFixture(): Promise<AppDataSnapshot> {
  const fixture = await import('../data/demo');
  return {
    ...emptySnapshot,
    member: { ...fixture.member, sponsorId: fixture.member.sponsorId, rank: 'Builder', status: 'active' },
    memberIds: [fixture.member.id],
    slots: fixture.slots.map((slot) => ({ ...slot, status: slot.active ? 'active' : 'locked' })),
    activity: fixture.activity.map((record) => ({ ...record, status: record.status === 'Finalized' ? 'Confirmed' : record.status, blockNumber: null })),
    chartData: fixture.chartData,
    partners: fixture.partners.map(({ branch: _branch, ...partner }) => ({
      ...partner,
      era: partner.era as Era,
      joined: new Date().toISOString(),
      status: 'active' as const,
    })),
    generationNodes: fixture.generationNodes,
    tokens: fixture.tokens.map((token) => ({ ...token, unlocks: token.unlocks, providerCode: null })),
    providers: fixture.providers.map((provider) => ({ ...provider, code: provider.icon, governanceStatus: 'approved' })),
    ranks: fixture.ranks.map((rank) => ({ ...rank, distributedAmount: '0', nextDistributionAt: new Date().toISOString() })),
    rpcProviders: fixture.rpcProviders.map((provider, index) => ({ ...provider, id: `fixture-${index}`, priority: index + 1, failures: 0, lastCheckedAt: null })),
    dashboardStats: { teamMembers: 1_284, directMembers: fixture.partners.length, completedCycles: 2, activeSlots: fixture.slots.filter(({ active }) => active).length, totalEarned: '0.0819', redeemable: '0' },
    publicStats: { totalMembers: 15_930, activeMembers: 12_492, completedCycles: 0, confirmedTransactions: fixture.activity.length, distributed: '8.384' },
    projectionNotice: 'Development-only fixture; blockchain state is authoritative.',
  };
}

export function AppDataProvider({ children }: { children: ReactNode }) {
  const wallet = useWallet();
  const [snapshot, setSnapshot] = useState<AppDataSnapshot>(emptySnapshot);
  const [mode, setMode] = useState<DataMode>('loading');
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  const [activeBdId, setActiveBdId] = useState<string | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [placementRules, setPlacementRules] = useState<PlacementRulesView>(DEFAULT_PLACEMENT_RULES);

  const refresh = useCallback(() => setRefreshVersion((value) => value + 1), []);
  const selectActiveId = useCallback((bdId: string) => setActiveBdId(bdId), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setMode('loading');
      setError(null);
      const publicRequests = await Promise.all([
        api<ApiPublicStats>('/api/public/stats').catch(() => null),
        api<ApiProvider[]>('/api/public/providers').catch(() => null),
        api<ApiRankPool[]>('/api/public/rank-pools').catch(() => null),
        api<ApiRpcHealth[]>('/api/public/rpc-health').catch(() => null),
        api<IndexerStatusView>('/api/public/indexer-status').catch(() => null),
        api<PlacementRulesView>('/api/public/placement-rules').catch(() => null),
      ]);
      if (!cancelled && publicRequests[5]) setPlacementRules(publicRequests[5]);

      try {
        const authenticated = wallet.authenticated;
        if (!authenticated && !import.meta.env.DEV) {
          if (!cancelled) {
            setSnapshot({
              ...emptySnapshot,
              publicStats: publicRequests[0] ? {
                totalMembers: publicRequests[0].totalMembers,
                activeMembers: publicRequests[0].activeMembers,
                completedCycles: publicRequests[0].completedCycles,
                confirmedTransactions: publicRequests[0].confirmedTransactions,
                distributed: atomicToDecimal(publicRequests[0].distributedAtomic),
              } : emptySnapshot.publicStats,
              providers: (publicRequests[1] ?? []).map((provider) => ({ code: provider.code, name: provider.name, icon: provider.code.slice(0, 2), multiplier: 'On-chain terms', model: provider.governanceStatus, eras: ['See active version'], active: provider.active, governanceStatus: provider.governanceStatus })),
              rpcProviders: (publicRequests[3] ?? []).map((provider) => ({ id: provider.id, name: provider.id, state: provider.healthy ? 'healthy' : 'degraded', latency: provider.latencyMs, traffic: 0, priority: provider.priority, failures: provider.consecutiveFailures, lastCheckedAt: provider.lastCheckedAt })),
              indexer: publicRequests[4] ?? { enabled: false },
            });
            setMode('empty');
            setRefreshedAt(new Date().toISOString());
          }
          return;
        }

        const prefix = authenticated ? '/api/me' : '/api/demo';
        const query = authenticated && activeBdId ? `?bdId=${encodeURIComponent(activeBdId)}` : '';
        const memberIds = authenticated
          ? (await api<Array<{ bdId: string }>>('/api/me/members')).map(({ bdId }) => bdId)
          : [];
        const [dashboard, network, cycles] = await Promise.all([
          api<ApiDashboard>(`${prefix}/dashboard${query}`),
          api<ApiNetwork>(`${prefix}/network${query}`),
          api<CycleView[]>(`${prefix}/cycles${query}`),
        ]);
        if (cancelled) return;
        setSnapshot(snapshotFromApi(
          dashboard,
          network,
          cycles,
          publicRequests[0],
          publicRequests[1],
          publicRequests[2],
          publicRequests[3],
          publicRequests[4],
          memberIds,
        ));
        setActiveBdId(dashboard.member.bdId);
        setMode(authenticated ? 'authenticated' : 'demo');
        setRefreshedAt(new Date().toISOString());
      } catch (cause) {
        if (cancelled) return;
        if (import.meta.env.DEV && !wallet.authenticated) {
          setSnapshot(await loadDevelopmentFixture());
          setMode('demo');
          setRefreshedAt(new Date().toISOString());
          return;
        }
        if (snapshot.member) {
          setMode('stale');
          setError(cause instanceof Error ? cause.message : 'Refresh failed; the last successfully read on-chain data remains visible.');
          return;
        }
        setSnapshot(emptySnapshot);
        setMode('error');
        setError(cause instanceof Error ? cause.message : 'The indexed on-chain data could not be loaded.');
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [activeBdId, refreshVersion, wallet.authenticated]);

  useEffect(() => {
    if (!wallet.authenticated) return;
    const interval = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(interval);
  }, [refresh, wallet.authenticated]);

  const matrixForCycle = useCallback((slotNumber: number, cycleNumber: number): MatrixNode[] => {
    const cycle = snapshot.cycles.find((item) => item.slotNumber === slotNumber && item.cycleNumber === cycleNumber);
    const positions = Math.max(12, cycle?.totalPositions ?? 12);
    return Array.from({ length: positions }, (_, index) => {
      const placement = cycle?.placements.find((item) => item.positionNumber === index + 1);
      return placement ? {
        position: index + 1,
        memberId: placement.memberBdId,
        // On-chain cycles carry the SeatFilled kind; colour it the way every position grid does.
        kind: placement.kind ? positionKindTone(placement.kind) : placement.placementType === 'virtual' ? 'treasury' : 'downline',
        state: 'confirmed',
      } : { position: index + 1, kind: 'empty', state: 'empty' };
    });
  }, [snapshot.cycles]);

  const value = useMemo<AppDataContextValue>(() => ({
    ...snapshot,
    mode,
    error,
    refreshedAt,
    activeBdId,
    selectActiveId,
    refresh,
    matrixForCycle,
    placementRules,
    club: snapshot.club,
  }), [activeBdId, error, matrixForCycle, mode, placementRules, refresh, refreshedAt, selectActiveId, snapshot]);

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppDataContextValue {
  const context = useContext(AppDataContext);
  if (!context) throw new Error('useAppData must be used inside AppDataProvider');
  return context;
}
